import { createReducer, on } from '@ngrx/store';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { departmentResponseToStoreDepartment } from './adapters/store-department.adapters';
import { departmentsActions } from './departments.actions';
import { DepartmentsState, initialDepartmentsState } from './departments.state';
import { StoreDepartmentMembership } from './models/store-department-membership';

// Departments load per client, so a mutation may target a client/department not yet in the store.
const isDepartmentLoaded = (state: DepartmentsState, clientCode: string, departmentId: number): boolean =>
  !!state.departments[clientCode]?.[departmentId];

export const departmentsReducer = createReducer(
  initialDepartmentsState,

  on(lifeCycleActions.resetState, () => initialDepartmentsState),

  on(departmentsActions.getDepartmentsSuccess, (state: DepartmentsState, { clientCode, departments }) => ({
    ...state,
    departments: {
      ...state.departments,
      [clientCode]: Object.fromEntries(
        departments.map((department) => [department.id, departmentResponseToStoreDepartment(department)]),
      ),
    },
  })),

  on(
    departmentsActions.getDepartmentMembershipsSuccess,
    (state: DepartmentsState, { clientCode, departmentId, memberships }) => {
      if (!isDepartmentLoaded(state, clientCode, departmentId)) {
        return state;
      }
      return {
        ...state,
        departments: {
          ...state.departments,
          [clientCode]: {
            ...state.departments[clientCode],
            [departmentId]: {
              ...state.departments[clientCode][departmentId],
              areMembershipsLoaded: true,
              memberships: memberships.reduce(
                (membershipAcc, membership) => ({
                  ...membershipAcc,
                  [membership.userId]: membership,
                }),
                {},
              ),
            },
          },
        },
      };
    },
  ),

  on(
    departmentsActions.updateDepartmentMembershipsSuccess,
    (state: DepartmentsState, { clientCode, departmentId, memberships }) => {
      if (!isDepartmentLoaded(state, clientCode, departmentId)) {
        return state;
      }
      return {
        ...state,
        departments: {
          ...state.departments,
          [clientCode]: {
            ...state.departments[clientCode],
            [departmentId]: {
              ...state.departments[clientCode][departmentId],
              areMembershipsLoaded: true,
              memberships: memberships.reduce(
                (membershipAcc, membership) => ({
                  ...membershipAcc,
                  [membership.userId]: membership,
                }),
                {},
              ),
            },
          },
        },
      };
    },
  ),

  on(
    departmentsActions.updateDepartmentSuccess,
    (state: DepartmentsState, { clientCode, departmentId, name, isOpen, medicalSpecialtyId }) => {
      if (!isDepartmentLoaded(state, clientCode, departmentId)) {
        return state;
      }
      return {
        ...state,
        departments: {
          ...state.departments,
          [clientCode]: {
            ...state.departments[clientCode],
            [departmentId]: {
              ...state.departments[clientCode][departmentId],
              name: name ?? state.departments[clientCode][departmentId].name,
              isOpen: isOpen ?? state.departments[clientCode][departmentId].isOpen,
              medicalSpecialtyId: medicalSpecialtyId ?? state.departments[clientCode][departmentId].medicalSpecialtyId,
            },
          },
        },
      };
    },
  ),

  on(departmentsActions.createDepartmentSuccess, (state: DepartmentsState, { department, clientCode }) => ({
    ...state,
    departments: {
      ...state.departments,
      [clientCode]: {
        ...state.departments[clientCode],
        [department.id]: departmentResponseToStoreDepartment(department),
      },
    },
  })),

  on(
    departmentsActions.updateDepartmentUserSuccess,
    (state: DepartmentsState, { clientCode, departmentId, departmentUserId, membership }) => {
      if (!isDepartmentLoaded(state, clientCode, departmentId)) {
        return state;
      }
      return {
        ...state,
        departments: {
          ...state.departments,
          [clientCode]: {
            ...state.departments[clientCode],
            [departmentId]: {
              ...state.departments[clientCode][departmentId],
              memberships: {
                ...state.departments[clientCode][departmentId].memberships,
                [departmentUserId]: {
                  ...state.departments[clientCode][departmentId].memberships[departmentUserId],
                  isAdmin: membership.isAdmin,
                  disabledOn: membership.disabledOn,
                },
              },
            },
          },
        },
      };
    },
  ),

  on(departmentsActions.removeUserMembership, (state, { clientCode, departmentId, userId }) => {
    if (!isDepartmentLoaded(state, clientCode, departmentId)) {
      return state;
    }
    const departmentUsers = state.departments?.[clientCode]?.[departmentId]?.memberships || {};
    const { [userId]: removedUser, ...remainingDepartmentUsers } = departmentUsers;
    return {
      ...state,
      departments: {
        ...state.departments,
        [clientCode]: {
          ...state.departments[clientCode],
          [departmentId]: {
            ...state.departments[clientCode][departmentId],
            memberships: remainingDepartmentUsers,
          },
        },
      },
    };
  }),

  on(departmentsActions.addUsersMembershipSuccess, (state, { clientCode, departmentId, userIds }) => {
    if (!isDepartmentLoaded(state, clientCode, departmentId)) {
      return state;
    }
    const currentMemberships = state.departments?.[clientCode]?.[departmentId]?.memberships || {};
    const addedMemberships = Object.fromEntries(
      userIds.map((userId) => [
        userId,
        {
          userId,
          departmentId,
          isAdmin: false,
          joinedOn: new Date(),
        },
      ]),
    ) as { [userId: number]: StoreDepartmentMembership };

    return {
      ...state,
      departments: {
        ...state.departments,
        [clientCode]: {
          ...state.departments[clientCode],
          [departmentId]: {
            ...state.departments[clientCode][departmentId],
            memberships: {
              ...currentMemberships,
              ...addedMemberships,
            },
          },
        },
      },
    };
  }),
);
